import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { Avatar } from '../components/Avatar';
import { Permission } from '../components/Permission';
import { SelfieSpot } from '../components/WhoAmI';
import { PhotoToDrawing } from '../components/PhotoToDrawing';
import { CREW } from '../data/crew';
import { getChallenge, headlineFor } from '../data/exercises';
import { dreamPhrase, dreamForIdea, dreamLabels, ideaOf, yourKind } from '../data/intro-copy';
import { IMAGINE, PLACE, PHOTO, LESSON, KEEPING, REVEAL, ROLL, dealSparks, dealRoll, ROLL_SLOTS, isMine, typedCount, pieceOf, prevLevel, rollSentence, rollPrint, shapeOf, placeOf, fillIn, lower, printWords, ideaWords, restoreRoll, type Roll, type RollWords } from '../data/imagine-copy';
import { IdeaRoll } from '../components/IdeaRoll';
import { IdeaPostcard } from '../components/IdeaPostcard';
import { answerCrew, sketchIdea } from '../lib/crew';
import { Darkroom } from '../components/Reveal';
import { renderCard } from '../card/render';
import { cardToPng } from '../card/toPng';
import { createPost, type CreatedPost } from '../lib/posts';
import { partsIn } from '../lib/pieces';
import { inAppBrowser } from '../lib/in-app';
import { clearImagineProgress, loadImagineProgress, saveImagineProgress, type ImagineIdea } from '../lib/imagine-progress';
import { ideaStep, placeStep } from '../lib/experiments';
import { currentDay, loadProfile, recordMade, saveProfile } from '../lib/state';
import { transition } from '../lib/transition';
import { setEventBase, track } from '../analytics';
import type { Work } from '../data/types';
import { useViewed } from '../lib/viewed';

export { IMAGINE };

type Step = 'place' | 'photo' | 'crew' | 'lesson' | 'making';

/**
 * Imagine Yourself There (well/product/33): the first activity for people an
 * ad sent with `?start=imagine`, in place of the talk warm-up.
 *
 * Paid visitors arrive inside Facebook's and Instagram's in-app browsers, and
 * the warm-up's first ask there is the microphone; on 2026-10-14 nobody who
 * reached it went past it. This asks for nothing a person on a bus with the
 * sound off cannot give:
 *
 *  1. place — dream up a place you'd like to be. Three dealt sparks (a place
 *     and a twist that do not belong together), so there is never a blank page
 *     and nothing typed can be worse than the first thing on the screen.
 *     In the `place_step` experiment's `machine` arm this is the Postcard
 *     idea roll instead (components/IdeaRoll.tsx, doc 33 §9): [who] at
 *     [where], [doing what], rolled, then written a row a level; "Make it"
 *     at the end starts the crew's quick sketch of it and goes straight to the
 *     darkroom (lean Day 1, owner 2026-10-30): no photo, no permission screen
 *     ("Make it" turns the crew on), no lesson. The sketch, a plain stand-in
 *     where you go, is the picture; the photo that puts you in it comes later.
 *  2. photo — "Make your first creation": the whole sum as an example (your
 *     photo + your place = your first creation, components/PhotoToDrawing),
 *     then a photo of you, front camera first, the library one line under it.
 *     Optional: without one the crew draw a character instead.
 *     2b. crew — the crew's permission, the existing screen, when not yet asked.
 *  3. lesson — "Allow yourself to imagine.", the kept screen's lesson bubble.
 *  4. then the reward screen (/next) as after any exercise, said as their
 *     first creation: the darkroom while the crew draw them in their place,
 *     the print, the turn, Kraben posting it, and then the email, asked as how
 *     you get back tomorrow (components/KeepAsk.tsx). It was asked here, before
 *     the darkroom, until 2026-10-19: one finisher in five gave it.
 *
 * The road is shown before all this, in the intro, in place of its three cards
 * (screens/Intro.tsx, owner 2026-10-14), so it is not shown again here.
 *
 * The post goes up in the background from step 2 on (the card, a postcard of
 * the place, and the photo as Day 2's "Who are you?" sends it), so the drawing
 * has usually begun before the lesson is read.
 * Every step is one screen pinned to the window (`Frame theme="fit"`), so a
 * tap inside Facebook's browser is never read as a scroll.
 */
export function Imagine() {
  const nav = useNavigate();
  const challenge = getChallenge(IMAGINE)!;
  // A reload (the camera can cause one inside Facebook's browser) resumes where they were, place and all.
  const [restored] = useState(() => loadImagineProgress());
  /**
   * The idea from the intro (the `idea_step` experiment, well/product/33 §8):
   * what kind of thing they want to make and what it is about. With one, there
   * is no place step: the crew draw them inside the idea's world. From the
   * reload record when there is one, else from the profile, in arm `on` only.
   */
  const [idea] = useState<ImagineIdea | undefined>(() => restored ? restored.idea : (ideaStep() === 'on' ? ideaOf(loadProfile()) : undefined));
  const [step, setStepRaw] = useState<Step>(() => restored?.step ?? (idea ? 'photo' : 'place'));
  const setStep = (s: Step) => transition(() => setStepRaw(s));
  const [place, setPlace] = useState(() => restored?.place ?? '');
  // the idea roll (the `place_step` experiment's `machine` arm): its blanks and level, and how many times a die was rolled.
  // "I don't have any" in the intro asks for it whatever the dice said (`machine` in the
  // navigation's state, which a reload keeps), and so does a reload that already had a roll.
  const noIdea = !!(useLocation().state as { machine?: boolean } | null)?.machine;
  const [placeArm] = useState<'machine' | 'box'>(() => (noIdea || restored?.roll ? 'machine' : placeStep()));
  const [roll, setRoll] = useState<Roll>(() => (restored?.roll ? restoreRoll(restored.roll) : dealRoll(dreamForIdea(loadProfile().dreams), loadProfile().dreamKind)));
  /** what the roll's idea is for: the kind they picked ("your podcast"), else their dream ("your writing"); nothing when neither */
  const rollGoal = (): string | undefined => { const kind = loadProfile().dreamKind; return kind ? yourKind(kind) : (roll.dream && ROLL.goalForDream[roll.dream]) || undefined; };
  // the roll's levels are steps of their own, so a funnel shows which level somebody stopped on
  useViewed('imagine', step === 'place' ? (placeArm === 'machine' ? `roll_level_${roll.level}` : 'place') : step);
  // the sentence they went on with: the photo step, the card and the crew's drawing take all of it
  const [rolled, setRolled] = useState<RollWords | undefined>(() => restored?.rolled);
  // the crew's quick sketch of it (backend kindling-sketch.service), started as the photo step opens
  const [sketch, setSketch] = useState<{ id: string; url: string } | 'pending' | 'failed' | undefined>(undefined);
  const sketching = useRef<Promise<{ id: string; url: string } | null> | null>(null);
  // the sentence the sketch on hand is of: going back and making a different one draws again
  const sketchedFor = useRef('');
  const spins = useRef(0);
  const [sparks, setSparks] = useState(() => dealSparks());
  const dealt = useRef(1);
  const fromSpark = useRef<string | null>(null);
  const [selfie, setSelfie] = useState<string | undefined>(undefined);
  const photoFrom = useRef<'camera' | 'library' | undefined>(undefined);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [post, setPost] = useState<'none' | 'saving' | 'kept' | 'failed'>('none');
  const [leaving, setLeaving] = useState(false);
  const keeping = useRef<Promise<CreatedPost | null> | null>(null);
  const pngUrl = useRef<string | undefined>(undefined);
  const startedAt = useRef(Date.now());
  const day = currentDay(loadProfile());

  useEffect(() => {
    setEventBase({ challengeId: IMAGINE, day, door: 'front' });
    track('imagine_started', { from: idea ? 'idea' : noIdea ? 'no_idea' : 'place', place_how: idea ? 'none' : placeArm === 'machine' ? 'roll' : 'box' });
    // and, when a reload brought them back, where to (counted on its own so the funnel's first step stays one event)
    if (restored) track('imagine_restored', { step: restored.step, place: !!restored.place.trim(), idea: !!restored.idea, ago_s: Math.round((Date.now() - restored.at) / 1000), in_app: inAppBrowser() ?? 'none' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // written as it changes, up to the moment something is made (keep() clears it)
  useEffect(() => { if (step === 'place' || step === 'photo') saveImagineProgress({ place, step, idea, ...(placeArm === 'machine' ? { roll } : {}), ...(rolled ? { rolled } : {}) }); }, [step, place, idea, roll, rolled, placeArm]);
  // the sketch is drawn while they look at "Make your first creation" (and again after a reload there: the same words are not drawn twice)
  useEffect(() => {
    if ((step !== 'photo' && step !== 'making') || !rolled) return;
    const scene = rollSentence(rolled, shapeOf(roll));
    if (sketchedFor.current === scene) return;
    sketchedFor.current = scene;
    const t0 = performance.now();
    setSketch('pending');
    const mine = sketchIdea(scene, dreamLabels(roll.dream ? [roll.dream] : loadProfile().dreams)[0])
      .then((s) => { if (sketching.current === mine) setSketch(s); track('imagine_sketch', { ok: true, ms: Math.round(performance.now() - t0) }); return s; })
      .catch((err: { code?: string; status?: number }) => { if (sketching.current === mine) setSketch('failed'); track('imagine_sketch', { ok: false, code: err?.code || String(err?.status || 'error'), ms: Math.round(performance.now() - t0) }); return null; });
    sketching.current = mine;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rolled]);
  // each step starts at its top: the inside of a fit screen can scroll on a short phone
  useEffect(() => { document.querySelector('.phone.fit .main')?.scrollTo?.(0, 0); }, [step]);

  /**
   * Keep it: the card (a postcard of the place; never the photo, which only the
   * crew see), then the post, which starts the crew's drawing on the backend
   * (a character exercise: drawn from the photo, kept as "me"). In the
   * background, once; leaving the lesson for the darkroom waits for it.
   */
  const keep = (p: string, photo: string | undefined): Promise<CreatedPost | null> => {
    if (keeping.current) return keeping.current;
    // from here the road records what was made, and a reload finds the print waiting instead
    clearImagineProgress();
    setPost('saving');
    const t0 = performance.now();
    const goals = dreamPhrase(loadProfile().dreams);
    // with an idea, the drawing's subject is the idea (kind + about), and there is no place;
    // after the idea roll it is the whole sentence, the place being its where
    // after the idea roll: the whole sentence (its shape varies), who is in it, and the place when its where is one
    const subject: Record<string, string> = idea ? { kind: idea.kind, about: idea.about } : rolled ? { scene: rollSentence(rolled, shapeOf(roll)), who: rolled.who, ...(shapeOf(roll).place ? { place: placeOf(rolled.where) } : {}) } : { place: p };
    const work: Work = { fields: { ...subject, ...(goals ? { what: goals } : {}) }, ...(photo ? { selfie: photo } : {}) };
    const durationSec = Math.round((Date.now() - startedAt.current) / 1000);
    keeping.current = (async () => {
      try {
        // the crew's finished drawing is built from the sketch: wait a little for one still on its way
        const sk = sketching.current ? await Promise.race([sketching.current, new Promise<null>((r) => setTimeout(() => r(null), 8000))]) : null;
        if (sk) work.fields = { ...work.fields, sketch: sk.id };
        const png = await cardToPng(renderCard({ challenge, deal: {}, work, day, durationSec, endedEarly: false }));
        pngUrl.current = URL.createObjectURL(png);
        recordMade(loadProfile(), day, IMAGINE);
        const created = await createPost({ challengeId: IMAGINE, deal: {}, work, day, durationSec, endedEarly: false, headline: headlineFor(challenge, {}), invite: false, card: png });
        recordMade(loadProfile(), day, IMAGINE, created.postId);
        setPost('kept');
        track('imagine_post_kept', { photo: !!photo, ms: Math.round(performance.now() - t0) });
        return created;
      } catch (err) {
        console.warn('[kindling] imagine: keeping the post failed', err);
        setPost('failed');
        track('imagine_post_failed', { error: String((err as Error)?.message || err).slice(0, 120) });
        keeping.current = null; // the lesson's button tries again
        return null;
      }
    })();
    return keeping.current;
  };

  // ── 3. the lesson, then 4. the darkroom ────────────────────────────────────
  /**
   * Into the reward, the way any exercise goes. The post is usually kept by
   * now (it went up from the photo step); if not, the button waits for it, and
   * a failure says so and lets them try again.
   */
  const toPicture = async () => {
    if (leaving) return;
    setLeaving(true);
    const created = await keep(place, selfie);
    track('imagine_print', { post: !!created, crew: loadProfile().crew || 'unasked' });
    if (!created) { setLeaving(false); return; }
    // The place fills the film's `somewhere`. An idea is not a place, and this
    // exercise gives only `somewhere`, so an idea goes into no slot (no parts):
    // a premise slot for it is a change to the exercise, left for later (doc 33 §8).
    // after the idea roll, only a shape whose where is a place gives one ("every Friday night" is not somewhere)
    const work: Work = { fields: idea || (rolled && !shapeOf(roll).place) ? {} : { place } };
    nav('/next', {
      viewTransition: true,
      state: { challengeId: IMAGINE, headline: headlineFor(challenge, {}), pngUrl: pngUrl.current, day, postId: created.postId, shareUrl: created.shareUrl, subject: idea ? idea.about : rolled ? rollSentence(rolled, shapeOf(roll)) : place, ideas: [idea ? ideaWords(idea.kind, !!selfie) : rolled ? rollPrint(rolled, !!selfie, shapeOf(roll)) : printWords(place, !!selfie)], parts: partsIn(challenge, work) },
    });
  };
  // After the idea roll's "Make it": the darkroom at once, while the sketch is drawn and the post goes up,
  // then the reward's own darkroom picks up where this one is.
  const making = useRef(false);
  useEffect(() => {
    if (step !== 'making' || making.current) return;
    making.current = true;
    void toPicture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── 1. an idea, on the idea roll ──────────────────────────────────────────
  if (step === 'place' && placeArm === 'machine') {
    // it follows from the question before: "I don't have any" (for your memoir) is answered here
    const firstLine = noIdea ? ROLL.noIdea : ROLL.start;
    // what it is all for, over everything: the kind they picked, else their dream
    const goalFor = rollGoal();
    const goal = goalFor ? fillIn(ROLL.goal, { yours: goalFor }) : ROLL.goalBare;
    const how = Object.fromEntries(ROLL_SLOTS.map((k) => [k, isMine(roll, k) ? 'typed' : 'rolled']));
    const onMake = (w: RollWords, made: 'made' | 'skipped') => {
      // the same event as the box's, so the funnel is one; `how` tells the arms apart
      track('imagine_place', { how: 'roll', made, level: roll.level, typed: typedCount(roll), ...how, shape: shapeOf(roll).id, dream: roll.dream ?? 'none', spins: spins.current, chars: rollSentence(w, shapeOf(roll)).length, ...(noIdea ? { from: 'no_idea' } : {}) });
      setRolled(w);
      setPlace(shapeOf(roll).place ? placeOf(w.where) : rollSentence(w, shapeOf(roll)));
      // "Make it" is the yes to the crew (lean Day 1, owner 2026-10-30): no permission screen in the way of the
      // picture, the darkroom says how to turn them off, and Friends has the switch. Off stays off.
      if ((loadProfile().crew || 'unasked') === 'unasked') {
        void answerCrew('on');
        saveProfile({ ...loadProfile(), crew: 'on' });
        track('crew_permission', { answer: 'on', how: 'make_it' });
      }
      // no photo, no lesson: the sketch is the picture, and the photo comes later, once there is an email
      setStep('making');
    };
    return (
      <Frame theme="fit roll-fit" onBack={roll.level > 1 ? () => { track('imagine_back', { from: `level_${roll.level}` }); setRoll(prevLevel(roll)); } : undefined} left={<span className="tag">Day {day} · {PLACE.tag}</span>} right={<span className="tag butter">{challenge.minutes} minutes</span>}>
        <IdeaRoll
          roll={roll}
          onRoll={setRoll}
          goal={goal}
          firstLine={firstLine}
          onMake={onMake}
          onSpin={(slot) => { spins.current += 1; track('imagine_spin', { slot, n: spins.current, level: roll.level }); }}
          onLevel={(n) => track('imagine_level', { level: n, from: roll.level, typed: typedCount(roll), ...how, spins: spins.current })}
        />
      </Frame>
    );
  }

  // ── 1. a place ─────────────────────────────────────────────────────────────
  if (step === 'place') {
    const ready = place.trim().length > 0;
    const takeSpark = (s: string) => { fromSpark.current = s; setPlace(s); track('imagine_spark', { spark: s }); };
    const onNext = () => {
      const p = place.trim().replace(/\s+/g, ' ').slice(0, 120);
      const how = fromSpark.current === null ? 'typed' : fromSpark.current === p ? 'spark' : 'mixed';
      track('imagine_place', { how, sparks: dealt.current * 3, chars: p.length });
      setPlace(p);
      setStep('photo');
    };
    return (
      <Frame theme="fit" left={<span className="tag">Day {day} · {PLACE.tag}</span>} right={<span className="tag butter">{challenge.minutes} minutes</span>}>
        <div className="row between"><span className="label">{PLACE.label}</span><KrabenMark size={56} /></div>
        <h1>{PLACE.title}</h1>
        <p className="why" style={{ fontSize: 17 }}>{PLACE.body}</p>
        <textarea
          className="input hand imagine-place"
          rows={2}
          value={place}
          maxLength={120}
          placeholder={PLACE.placeholder}
          aria-label="Your place"
          enterKeyHint="done"
          onChange={(e) => setPlace(e.target.value.replace(/\n/g, ' '))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (ready) onNext(); } }}
        />
        <div className="stack" style={{ gap: 8 }}>
          <span className="label">{PLACE.sparksLabel}</span>
          <div className="sparks">
            {sparks.map((s) => <button key={s} type="button" className={`chip spark ${place === s ? 'on' : ''}`} onClick={() => takeSpark(s)}>{s}</button>)}
          </div>
          <button type="button" className="link" style={{ alignSelf: 'flex-start' }} onClick={() => { dealt.current += 1; setSparks(dealSparks(sparks)); track('imagine_deal_again', { n: dealt.current }); }}>🎲 {PLACE.again}</button>
        </div>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn coral" disabled={!ready} onClick={onNext}>{PLACE.done}</button>
        </div>
      </Frame>
    );
  }

  // ── 2. the darkroom, straight after "Make it" ─────────────────────────────
  if (step === 'making') {
    const goalFor = rollGoal();
    if (post === 'failed' && !leaving) return <Lesson day={day} busy={false} more={goalFor ? fillIn(LESSON.moreRoll, { yours: goalFor }) : LESSON.moreRollBare} failed onNext={() => { void toPicture(); }} />;
    return <Darkroom day={day} words={rolled ? rollPrint(rolled, false, shapeOf(roll)) : place} says={LESSON.making} label={REVEAL.label} note={loadProfile().crew === 'on' ? LESSON.crewNote : undefined} />;
  }

  // ── 2. you ─────────────────────────────────────────────────────────────────
  if (step === 'photo') {
    const go = (withPhoto: boolean) => {
      const photo = withPhoto ? selfie : undefined;
      track('imagine_photo', { photo: !!photo, ...(photo ? { from: photoFrom.current } : {}) });
      if (!withPhoto) setSelfie(undefined);
      // the crew draw nothing until they are allowed to, and with them off the photo is not even kept
      if ((loadProfile().crew || 'unasked') === 'unasked') { setStep('crew'); return; }
      void keep(place, photo);
      setStep('lesson');
    };
    return (
      <Frame theme="fit imagine-photo" onBack={rolled ? () => { track('imagine_back', { from: 'photo' }); setStep('place'); } : undefined} left={<span className="tag">Day {day} · {PLACE.tag}</span>}>
        <h1>{PHOTO.title}</h1>
        {rolled ? (
          // after the idea roll: their postcard, which develops into the crew's sketch of it, you a plain stand-in in it
          // while the sketch is on its way the card lies in a tray of developer, as the print does in the darkroom
          <div className={`imagine-sketch ${sketch === 'pending' ? 'in-tray' : ''}`}>
            {sketch === 'pending' && (
              <>
                <span className="tray-liquid" aria-hidden="true"><span className="ripple" /><span className="ripple r2" /></span>
                <img className="tray-peek" src="/art/reveal/moon-top.webp" alt="" draggable={false} />
                <p className="bubble tray-says" role="status">{PHOTO.sketching}<span className="dots3"><i /><i /><i /></span></p>
              </>
            )}
            <IdeaPostcard
              who={pieceOf(roll, 'who')} where={pieceOf(roll, 'where')} doing={pieceOf(roll, 'doing')}
              caption={rollSentence(rolled, shapeOf(roll))} label={rollSentence(rolled, shapeOf(roll))}
              picture={typeof sketch === 'object' ? sketch.url : undefined}
              developing={sketch === 'pending'}
            />
          </div>
        ) : <PhotoToDrawing idea={!!idea} />}
        <p className="why" style={{ fontSize: 16 }}>{idea ? fillIn(PHOTO.ideaBody, { yours: yourKind(idea.kind) }) : rolled ? PHOTO.rollBody : fillIn(PHOTO.body, { place: lower(place) })}</p>
        <div className="imagine-selfie">
          <SelfieSpot selfie={selfie} camera source="imagine" onBusy={setPhotoBusy} onSelfie={(s, from) => { setSelfie(s); photoFrom.current = from; }} />
        </div>
        <p className="hint" style={{ textAlign: 'center' }}>{PHOTO.privacy}</p>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn coral" disabled={!selfie || photoBusy} onClick={() => go(true)}>{PHOTO.done}</button>
          <div className="row" style={{ justifyContent: 'center' }}>
            <button className="link" onClick={() => go(false)}>{rolled ? PHOTO.rollSkip : PHOTO.skip}</button>
          </div>
        </div>
      </Frame>
    );
  }

  // ── 2b. the crew's permission, once, on its own screen (as after any first card) ──
  if (step === 'crew') {
    return <Permission day={day} onAnswer={() => { void keep(place, selfie); setStep('lesson'); }} />;
  }

  // ── 3. the lesson ──────────────────────────────────────────────────────────
  const busy = leaving && post !== 'failed';
  const goalFor = rolled ? rollGoal() : undefined;
  const more = !rolled ? LESSON.more : goalFor ? fillIn(LESSON.moreRoll, { yours: goalFor }) : LESSON.moreRollBare;
  return <Lesson day={day} busy={busy} more={more} failed={post === 'failed' && !leaving} onNext={() => { track('imagine_lesson'); void toPicture(); }} />;
}

/** "Allow yourself to imagine.", said by Kraben, the way the kept screen says what an exercise grew. */
function Lesson({ day, busy, more, failed, onNext }: { day: number; busy: boolean; more: string; failed: boolean; onNext: () => void }) {
  const k = CREW.kraben;
  return (
    <Frame theme="fit" left={<span className="tag">Day {day} · {PLACE.tag}</span>}>
      <div className="lesson">
        <p className="lesson-bubble">{LESSON.line}</p>
        <Avatar who={k.id} size={176} />
        <div className="lesson-who"><b>{k.name}</b><span className="hint">{k.part}</span></div>
        <p className="why" style={{ fontSize: 17, maxWidth: 320 }}>{more}</p>
      </div>
      <div className="stack">
        {failed && <p className="hint" role="alert" style={{ textAlign: 'center' }}>{KEEPING.failed}</p>}
        <button className="btn" disabled={busy} onClick={onNext}>{busy ? KEEPING.waiting : LESSON.done}</button>
      </div>
    </Frame>
  );
}
